import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"

import { useScopedStorageKey } from "@/workspaces/scope"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { useFilterParams } from "@/hooks/filter-params/useFilterParams"
import type { MetadataFilter } from "@/types/MetadataFilter"
import { TIMESTAMP_MODE } from "@/types/tables/ConversationsTable"

import { useConversationsFilterActions } from "../hooks/useConversationsFilterActions"
import { useConversationsViewState } from "../hooks/useConversationsViewState"
import type { MetricValueFilter } from "../types"
import type { ColumnCallbacks, FilterState } from "../types/filterState"
import { type SavedView, type SavedViewConfig } from "../types/savedViews"
import type { SortDescriptor } from "../types/sortDescriptor"
import {
  CONVERSATIONS_OWNED_URL_KEYS,
  CONVERSATIONS_URL_DEFAULTS,
  CONVERSATIONS_URL_SCHEMA,
  type ConversationsUrlState,
} from "../utils/conversationsUrlFilters"

// ── Context interface ────────────────────────────────────────

interface ConversationsFilterContextType {
  state: FilterState
  handlerSetDateRange: (start: Date | null, end: Date | null) => void
  handlerClearDateRange: () => void
  handlerSetMetadataFilters: (filters: MetadataFilter[]) => void
  handlerAddMetadataFilter: (
    key: string,
    value: string,
    matchType?: MetadataFilter["matchType"],
    values?: string[],
  ) => void
  handlerRemoveMetadataFilter: (index: number) => void
  handlerClearMetadataFilters: () => void
  handlerSetSelectedMetric: (metric: string) => void
  handlerSetSortDescriptor: (sortDescriptor: SortDescriptor) => void
  handlerAddMetricValueFilter: (filter: MetricValueFilter) => void
  handlerRemoveMetricValueFilter: (metricId: string) => void
  handlerClearMetricValueFilters: () => void
  setMetricCondition: (
    metricId: string,
    filter: MetricValueFilter | null | undefined,
  ) => void
  resetMetricConditions: () => void
  setMetricIds: (metricIds: string[]) => void
  setAgentIds: (agentIds: string[]) => void
  setTagFilters: (tagFilters: string[]) => void
  setProjectIds: (projectIds: string[]) => void
  clearAllFilters: () => void
  totalActiveFilters: number
  hasActiveFilters: boolean
  /** Whether URL/localStorage hydration is complete */
  isReady: boolean

  // ── View state ─────────────────────────────────────────────
  loadedViewId: string | null
  /** Apply a saved view's config and show a toast. */
  loadView: (view: SavedView) => void
  /** Apply a view config without toast (for revert, internal use). */
  applyViewConfig: (config: SavedViewConfig) => void
  /** Clear the loaded view and reset to defaults. */
  clearView: () => void
  /** Silently detach the loaded view ID without resetting filters/columns. */
  detachView: () => void
  /** Register column getters/setters from ConversationsTable. Returns cleanup fn. */
  registerColumnCallbacks: (callbacks: ColumnCallbacks) => () => void
}

const ConversationsFilterContext = createContext<
  ConversationsFilterContextType | undefined
>(undefined)

// ── Provider ──────────────────────────────────────────────────

export const ConversationsFilterProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const { trackEvent } = useAnalytics()

  // Scoped by org + workspace so switching either can never resurrect a
  // filter (including a custom metric that only exists elsewhere) from the
  // previous scope.
  const filtersStorageKey = useScopedStorageKey("monitoring-url-filters")

  // URL-synced filters
  const {
    state: urlState,
    setField: setUrlField,
    setFields: setUrlFields,
    updateField: updateUrlField,
    resetAll: resetUrlFilters,
    isReady,
  } = useFilterParams<ConversationsUrlState>({
    schema: CONVERSATIONS_URL_SCHEMA,
    defaults: CONVERSATIONS_URL_DEFAULTS,
    storageKey: filtersStorageKey,
  })
  const [selectedMetric, setSelectedMetric] = useState("")
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: TIMESTAMP_MODE.CREATED_AT,
    direction: "descending",
  })

  // Detect shared link on first render (before router processes params).
  const isSharedLink = useMemo(() => {
    if (typeof window === "undefined") {
      return false
    }
    const params = new URLSearchParams(window.location.search)
    return CONVERSATIONS_OWNED_URL_KEYS.some((k) => params.has(k))
  }, [])

  // ── Composed state ─────────────────────────────────────────

  const composedState: FilterState = useMemo(
    () => ({
      dateRange: urlState.dateRange,
      metadataFilters: urlState.metadataFilters,
      metricValueFilters: urlState.metricValueFilters,
      selectedMetric,
      sortDescriptor,
      metricIds: urlState.metricIds,
      agentIds: urlState.agentIds,
      tagFilters: urlState.tagFilters,
      projectIds: urlState.projectIds,
    }),
    [
      urlState.dateRange,
      urlState.metricIds,
      urlState.agentIds,
      urlState.tagFilters,
      urlState.projectIds,
      urlState.metadataFilters,
      urlState.metricValueFilters,
      selectedMetric,
      sortDescriptor,
    ],
  )

  const {
    totalActiveFilters,
    hasActiveFilters,
    handlerSetDateRange,
    handlerClearDateRange,
    handlerSetMetadataFilters,
    handlerAddMetadataFilter,
    handlerRemoveMetadataFilter,
    handlerClearMetadataFilters,
    handlerAddMetricValueFilter,
    handlerRemoveMetricValueFilter,
    handlerClearMetricValueFilters,
    handlerSetSelectedMetric,
    handlerSetSortDescriptor,
    setMetricIds: setConversationsMetricIds,
    setAgentIds: setConversationsAgentIds,
    setTagFilters: setConversationsTagFilters,
    setProjectIds: setConversationsProjectIds,
    clearAllFilters,
  } = useConversationsFilterActions({
    urlState,
    setDateRange: (value) => setUrlField("dateRange", value),
    setMetadataFiltersField: (value) => setUrlField("metadataFilters", value),
    updateMetadataFilters: (updater) =>
      updateUrlField("metadataFilters", updater),
    setMetricValueFiltersField: (value) =>
      setUrlField("metricValueFilters", value),
    updateMetricValueFilters: (updater) =>
      updateUrlField("metricValueFilters", updater),
    setMetricIdsField: (value) => setUrlField("metricIds", value),
    setAgentIdsField: (value) => setUrlField("agentIds", value),
    setTagFiltersField: (value) => setUrlField("tagFilters", value),
    setProjectIdsField: (value) => setUrlField("projectIds", value),
    resetUrlFilters,
    setSelectedMetric,
    setSortDescriptor,
    trackEvent,
  })

  const {
    loadedViewId,
    registerColumnCallbacks,
    applyViewConfig,
    loadView,
    clearView,
    detachView,
  } = useConversationsViewState({
    isReady,
    isSharedLink,
    filterState: composedState,
    setUrlFields,
    resetUrlFilters,
    resetSelectedMetric: () => setSelectedMetric(""),
    setSortDescriptor,
  })

  // null preserves the legacy presence predicate; undefined removes the condition.
  const setMetricCondition = useCallback(
    (metricId: string, filter: MetricValueFilter | null | undefined) => {
      if (filter) {
        trackEvent(
          TRACKING_EVENTS.UPLOADED_CONVERSATIONS.METRIC_VALUE_FILTER_APPLIED,
          {
            metric_id: metricId,
            operator: filter.operator,
            value: filter.value,
            value_type: filter.valueType,
          },
        )
      } else if (
        urlState.metricValueFilters.some((item) => item.metricId === metricId)
      ) {
        trackEvent(
          TRACKING_EVENTS.UPLOADED_CONVERSATIONS.METRIC_VALUE_FILTER_REMOVED,
          { metric_id: metricId },
        )
      }
      setUrlFields({
        metricIds: [
          ...urlState.metricIds.filter((id) => id !== metricId),
          ...(filter === null ? [metricId] : []),
        ],
        metricValueFilters: [
          ...urlState.metricValueFilters.filter(
            (item) => item.metricId !== metricId,
          ),
          ...(filter ? [filter] : []),
        ],
      })
    },
    [setUrlFields, urlState.metricIds, urlState.metricValueFilters, trackEvent],
  )
  const resetMetricConditions = useCallback(() => {
    setUrlFields({ metricIds: [], metricValueFilters: [] })
  }, [setUrlFields])

  // ── Context value ──────────────────────────────────────────

  const contextValue = useMemo(
    () => ({
      state: composedState,
      setMetricCondition,
      resetMetricConditions,
      handlerSetDateRange,
      handlerClearDateRange,
      handlerSetMetadataFilters,
      handlerAddMetadataFilter,
      handlerRemoveMetadataFilter,
      handlerClearMetadataFilters,
      handlerAddMetricValueFilter,
      handlerRemoveMetricValueFilter,
      handlerClearMetricValueFilters,
      handlerSetSelectedMetric,
      handlerSetSortDescriptor,
      setMetricIds: setConversationsMetricIds,
      setAgentIds: setConversationsAgentIds,
      setTagFilters: setConversationsTagFilters,
      setProjectIds: setConversationsProjectIds,
      clearAllFilters,
      totalActiveFilters,
      hasActiveFilters,
      isReady,
      // View state
      loadedViewId,
      loadView,
      applyViewConfig,
      clearView,
      detachView,
      registerColumnCallbacks,
    }),
    [
      composedState,
      setMetricCondition,
      resetMetricConditions,
      isReady,
      handlerSetDateRange,
      handlerClearDateRange,
      handlerSetMetadataFilters,
      handlerAddMetadataFilter,
      handlerRemoveMetadataFilter,
      handlerClearMetadataFilters,
      handlerAddMetricValueFilter,
      handlerRemoveMetricValueFilter,
      handlerClearMetricValueFilters,
      handlerSetSelectedMetric,
      handlerSetSortDescriptor,
      setConversationsMetricIds,
      setConversationsAgentIds,
      setConversationsTagFilters,
      setConversationsProjectIds,
      clearAllFilters,
      totalActiveFilters,
      hasActiveFilters,
      // View state
      loadedViewId,
      loadView,
      applyViewConfig,
      clearView,
      detachView,
      registerColumnCallbacks,
    ],
  )

  return (
    <ConversationsFilterContext.Provider value={contextValue}>
      {children}
    </ConversationsFilterContext.Provider>
  )
}

export const useConversationsFilter = () => {
  const context = useContext(ConversationsFilterContext)
  if (context === undefined) {
    throw new Error(
      "useConversationsFilter must be used within a ConversationsFilterProvider",
    )
  }
  return context
}
